import { useContext, createContext, useMemo } from 'react';
import { Select } from '@zoopla-eng/zds-components/select';
import { buildSelectBedsTitle } from '@zoopla/redux-reducer-filters';
import { SelectGroup } from './SelectGroup';
import { SearchBarContext } from './SearchBar';
import { coerceQueryParameter } from '../utils/coerceQueryParameter';

const translationsMap = new Map([
    ['studio', 'Studio'],
    ['studios', 'Studios'],
    ['bed', 'bed'],
    ['beds', 'beds'],
    ['min', 'min'],
    ['max', 'max'],
    ['default', 'Any beds']
]);

const beds = ['', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];

const bedsOptions = [
    { value: '0', text: 'Studio' },
    { value: '1', text: '1' },
    { value: '2', text: '2' },
    { value: '3', text: '3' },
    { value: '4', text: '4' },
    { value: '5', text: '5' },
    { value: '6', text: '6' },
    { value: '7', text: '7' },
    { value: '8', text: '8' },
    { value: '9', text: '9' },
    { value: '10', text: '10+' }
];

export type BedsMin = (typeof beds)[number];
export type BedsMax = (typeof beds)[number];

type BedValue = {
    bedsMin: BedsMin;
    bedsMax: BedsMax;
};

type BedContext = {
    value: BedValue;
    onChange?: (props: BedValue) => void;
    dropdownValue: string;
};

const initialContextValue: BedContext = {
    value: {
        bedsMin: '',
        bedsMax: ''
    },
    onChange: undefined,
    dropdownValue: 'Any beds'
};

const BedsContext = createContext<BedContext>(initialContextValue);

export const SelectBeds: React.FC<{ children: React.ReactNode }> = ({
    children
}) => {
    const { formValue, updateFormValue } = useContext(SearchBarContext);

    const value = useMemo(
        () => ({
            bedsMin: coerceQueryParameter(formValue.beds_min),
            bedsMax: coerceQueryParameter(formValue.beds_max)
        }),
        [formValue.beds_max, formValue.beds_min]
    );

    const dropdownValue = buildSelectBedsTitle(
        parseInt(value.bedsMin, 10),
        parseInt(value.bedsMax, 10),
        translationsMap
    );

    const memoizedContextValue = useMemo(() => {
        const onChange = ({ bedsMin, bedsMax }: BedValue) => {
            updateFormValue?.((oldFormValue) => ({
                ...oldFormValue,
                beds_min: bedsMin,
                beds_max: bedsMax
            }));
        };

        return {
            dropdownValue,
            onChange,
            value
        };
    }, [dropdownValue, updateFormValue, value]);

    return useMemo(
        () => (
            <BedsContext.Provider value={memoizedContextValue}>
                {children}
            </BedsContext.Provider>
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [value]
    );
};

export const SelectMinBeds: React.FC = () => {
    const { value, onChange } = useContext(BedsContext);

    const bedsMinOptions =
        value.bedsMax !== ''
            ? bedsOptions.slice(0, Number(value.bedsMax) + 1)
            : bedsOptions;

    const minBedOptions = bedsMinOptions.map((bedsMinOption) => ({
        id: bedsMinOption.value,
        text: bedsMinOption.text
    }));

    const minDefaultOption = useMemo(
        () => ({
            id: '',
            text: 'No min'
        }),
        []
    );

    const selectedOption = useMemo(() => {
        return (
            minBedOptions.find((option) => option.id === value.bedsMin) ??
            minDefaultOption
        );
    }, [minBedOptions, minDefaultOption, value.bedsMin]);

    return (
        <Select
            options={[minDefaultOption, ...minBedOptions]}
            onSelectedOptionChange={(o) => {
                onChange &&
                    onChange({
                        ...value,
                        bedsMin: o.id
                    });
            }}
            selectedOption={selectedOption}
            label="Min beds"
            data-testid="min_beds_testid"
        />
    );
};

export const SelectMaxBeds: React.FC = () => {
    const { value, onChange } = useContext(BedsContext);

    const bedsMaxOptions =
        value.bedsMin !== ''
            ? bedsOptions.slice(Number(value.bedsMin))
            : bedsOptions;

    const maxBedOptions = bedsMaxOptions.map((bedsMaxOption) => ({
        id: bedsMaxOption.value,
        text: bedsMaxOption.text
    }));

    const maxDefaultOption = useMemo(
        () => ({
            id: '',
            text: 'No max'
        }),
        []
    );

    const selectedOption = useMemo(() => {
        return (
            maxBedOptions.find((option) => option.id === value.bedsMax) ??
            maxDefaultOption
        );
    }, [maxBedOptions, maxDefaultOption, value.bedsMax]);

    return (
        <Select
            options={[maxDefaultOption, ...maxBedOptions]}
            onSelectedOptionChange={(o) => {
                onChange &&
                    onChange({
                        ...value,
                        bedsMax: o.id
                    });
            }}
            selectedOption={selectedOption}
            label="Max beds"
            data-testid="max_beds_testid"
        />
    );
};

export const SelectGroupBeds: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => {
    const { dropdownValue } = useContext(BedsContext);

    return (
        <SelectGroup title="Bedrooms" value={dropdownValue}>
            {children}
        </SelectGroup>
    );
};
